import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { AppLayout } from "./DashboardPage";
import { TransactionsContent } from "./TransactionsPage";
import { TransfersContent } from "./TransfersPage";
import { CreditCard, ArrowLeftRight, ListChecks } from "lucide-react";
import { GuidanceBanner } from "../components/guidance/GuidanceBanner";
import { useGuidance } from "../components/guidance/GuidanceProvider";

const TABS = [
  { key: "transactions", label: "Transactions", icon: CreditCard },
  { key: "transfers", label: "Transfers", icon: ArrowLeftRight },
];

export default function TheFlowPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "transactions");
  const { state: guidance } = useGuidance();

  const pendingReviews = guidance?.nudges?.pending_reviews || 0;
  const userState = guidance?.user_state;

  useEffect(() => {
    const tabParam = searchParams.get("tab") || "transactions";
    if (tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const switchTab = (key) => {
    setActiveTab(key);
    if (key === "transactions") {
      setSearchParams({});
    } else {
      setSearchParams({ tab: key });
    }
  };

  return (
    <AppLayout>
      <div data-testid="the-flow-page">
        {/* Tab navigation */}
        <div className="border-b border-border bg-card/50 sticky top-0 z-10 backdrop-blur-sm">
          <div className="flex items-center gap-2 px-6 py-3">
            <h1 className="font-heading text-lg font-semibold text-foreground mr-4">The Flow</h1>
            <div className="flex items-center gap-1.5 bg-muted/50 rounded-lg p-1" data-testid="flow-tabs">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => switchTab(tab.key)}
                    className={`flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-md transition-all duration-150 cursor-pointer ${
                      isActive
                        ? "bg-blue-600 text-white shadow-sm"
                        : "text-muted-foreground hover:bg-blue-50 dark:hover:bg-blue-950/30 hover:text-blue-700 dark:hover:text-blue-300"
                    }`}
                    data-testid={`flow-tab-${tab.key}`}
                  >
                    <tab.icon className="w-3.5 h-3.5" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Contextual guidance */}
        {activeTab === "transactions" && pendingReviews > 0 && (
          <div className="px-6 lg:px-8 pt-2">
            <GuidanceBanner
              hintId="flow-pending-reviews"
              icon={ListChecks}
              message={
                userState === "overwhelmed"
                  ? "No need to do them all. Just sort one — that's enough for now."
                  : `You have ${pendingReviews} transaction${pendingReviews !== 1 ? 's' : ''} that could use a quick look. Take a minute to sort them — it'll feel good.`
              }
              show={pendingReviews > 3}
            />
          </div>
        )}

        {/* Tab content */}
        {activeTab === "transactions" && <TransactionsContent embedded />}
        {activeTab === "transfers" && <TransfersContent />}
      </div>
    </AppLayout>
  );
}
